import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// NAAVE Brand Colors
const COLORS = {
  primary: [15, 23, 42],      // slate-900
  secondary: [100, 116, 139], // slate-500
  accent: [245, 158, 11],     // amber-500
  white: [255, 255, 255],
  lightGray: [248, 250, 252], // slate-50
  border: [226, 232, 240]     // slate-200
};

const PERSON_TYPES = {
  propietario: 'Propietario',
  inquilino: 'Inquilino',
  facility_manager: 'Facility Manager',
  pagos: 'Pagos',
  contabilidad: 'Contabilidad',
  encargado: 'Encargado',
  desarrollador: 'Desarrollador',
  proveedor: 'Proveedor',
  otro: 'Otro'
};

/**
 * Generate Directory PDF
 */
export const generateDirectoryPDF = (parkName, persons, lots) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  
  // Helper to get lot numbers for a person
  const getPersonLots = (person) => {
    if (person.todos_los_lotes) return 'Todos los lotes';
    if (!person.lotes_asignados || person.lotes_asignados.length === 0) return 'Sin asignar';
    const personLots = lots.filter(l => person.lotes_asignados.includes(l.id));
    return personLots.map(l => l.numero_lote).join(', ') || 'Sin asignar';
  };

  // ========== HEADER ==========
  // Background bar
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  // Accent line
  doc.setFillColor(...COLORS.accent);
  doc.rect(0, 40, pageWidth, 3, 'F');
  
  // Logo text
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('NAAVE.', margin, 25);
  
  // Document title
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Directorio de Personas', pageWidth - margin, 20, { align: 'right' });
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.accent);
  doc.text(parkName, pageWidth - margin, 30, { align: 'right' });
  
  // ========== SUMMARY ==========
  let y = 55;
  
  // Summary cards
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(margin, y, contentWidth, 25, 2, 2, 'F');
  
  const cardWidth = contentWidth / 4;
  
  // Total Personas
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  doc.text('Total Personas', margin + 10, y + 8);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text(persons.length.toString(), margin + 10, y + 18);
  
  // Propietarios
  const propietarios = persons.filter(p => p.tipo === 'propietario').length;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  doc.text('Propietarios', margin + cardWidth + 10, y + 8);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text(propietarios.toString(), margin + cardWidth + 10, y + 18);
  
  // Inquilinos
  const inquilinos = persons.filter(p => p.tipo === 'inquilino').length;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  doc.text('Inquilinos', margin + (cardWidth * 2) + 10, y + 8);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text(inquilinos.toString(), margin + (cardWidth * 2) + 10, y + 18);
  
  // Otros
  const otros = persons.length - propietarios - inquilinos;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  doc.text('Otros', margin + (cardWidth * 3) + 10, y + 8);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text(otros.toString(), margin + (cardWidth * 3) + 10, y + 18);
  
  y += 35;
  
  // ========== TABLE ==========
  // Prepare table data
  const tableData = persons.map(person => [
    person.nombre || '',
    PERSON_TYPES[person.tipo] || person.tipo || '',
    person.email || '-',
    person.telefono || '-',
    person.empresa || '-',
    getPersonLots(person)
  ]);
  
  autoTable(doc, {
    startY: y,
    head: [['Nombre', 'Tipo', 'Email', 'Teléfono', 'Empresa', 'Lotes']],
    body: tableData,
    theme: 'plain',
    styles: {
      fontSize: 9,
      cellPadding: 4,
      lineColor: COLORS.border,
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold',
      fontSize: 9
    },
    alternateRowStyles: {
      fillColor: COLORS.lightGray
    },
    columnStyles: {
      0: { cellWidth: 35, fontStyle: 'bold' },
      1: { cellWidth: 25 },
      2: { cellWidth: 45 },
      3: { cellWidth: 25 },
      4: { cellWidth: 30 },
      5: { cellWidth: 'auto' }
    },
    margin: { left: margin, right: margin, top: 25 },
    didDrawPage: (data) => {
      // Footer on each page
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.secondary);
      doc.text(
        `Generado el ${new Date().toLocaleDateString('es-MX')} - Página ${data.pageNumber}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
      
      // Add header on subsequent pages
      if (data.pageNumber > 1) {
        doc.setFillColor(...COLORS.primary);
        doc.rect(0, 0, pageWidth, 15, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.white);
        doc.text('NAAVE. - Directorio de Personas', margin, 10);
        doc.setFont('helvetica', 'normal');
        doc.text(parkName, pageWidth - margin, 10, { align: 'right' });
      }
    }
  });
  
  // Save the PDF
  const fileName = `Directorio_${parkName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

/**
 * Generate Directory Excel
 */
export const generateDirectoryExcel = (parkName, persons, lots) => {
  // Helper to get lot numbers for a person
  const getPersonLots = (person) => {
    if (person.todos_los_lotes) return 'Todos los lotes';
    if (!person.lotes_asignados || person.lotes_asignados.length === 0) return 'Sin asignar';
    const personLots = lots.filter(l => person.lotes_asignados.includes(l.id));
    return personLots.map(l => l.numero_lote).join(', ') || 'Sin asignar';
  };

  // Prepare data
  const data = persons.map(person => ({
    'Nombre': person.nombre || '',
    'Tipo': PERSON_TYPES[person.tipo] || person.tipo || '',
    'Email': person.email || '',
    'Teléfono': person.telefono || '',
    'Empresa': person.empresa || '',
    'Lotes Asignados': getPersonLots(person),
    'Notas': person.notas || ''
  }));

  // Create workbook
  const wb = XLSX.utils.book_new();
  
  // Create summary sheet
  const summaryData = [
    ['NAAVE - Directorio de Personas'],
    [''],
    ['Parque:', parkName],
    ['Fecha de generación:', new Date().toLocaleDateString('es-MX')],
    [''],
    ['Resumen:'],
    ['Total de personas:', persons.length],
    ['Propietarios:', persons.filter(p => p.tipo === 'propietario').length],
    ['Inquilinos:', persons.filter(p => p.tipo === 'inquilino').length],
    ['Facility Managers:', persons.filter(p => p.tipo === 'facility_manager').length],
    ['Otros:', persons.filter(p => p.tipo === 'otro').length]
  ];
  
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  
  // Style summary sheet
  summarySheet['!cols'] = [{ wch: 25 }, { wch: 30 }];
  
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Resumen');
  
  // Create directory sheet
  const directorySheet = XLSX.utils.json_to_sheet(data);
  
  // Set column widths
  directorySheet['!cols'] = [
    { wch: 30 }, // Nombre
    { wch: 15 }, // Tipo
    { wch: 35 }, // Email
    { wch: 15 }, // Teléfono
    { wch: 25 }, // Empresa
    { wch: 30 }, // Lotes
    { wch: 40 }  // Notas
  ];
  
  XLSX.utils.book_append_sheet(wb, directorySheet, 'Directorio');
  
  // Save file
  const fileName = `Directorio_${parkName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);
};
